import { formatDate, formatExchangeLabel, formatNumber } from "../lib/format";
import type { DashboardManifest, DatasetHealth } from "../lib/types";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { StatusBadge } from "./ui/status-badge";

interface CoverageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  manifest: DashboardManifest | null;
  health: DatasetHealth | null;
  loadedRowCount: number;
  loadedShardCount: number;
}

function AuditRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-1.5 last:border-b-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function AuditGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-surface-inset p-3">
      <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <dl className="text-xs">{children}</dl>
    </section>
  );
}

export function CoverageSheet({
  open,
  onOpenChange,
  manifest,
  health,
  loadedRowCount,
  loadedShardCount
}: CoverageSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Coverage audit</SheetTitle>
          <SheetDescription>
            Manifest, shard, validation, and declared SEC universe reconciliation.
          </SheetDescription>
        </SheetHeader>

        {!manifest || !health ? (
          <div className="p-5 text-sm text-muted-foreground">
            Coverage details become available after the dataset manifest and all required shards are
            validated.
          </div>
        ) : (
          <div className="space-y-3 p-5">
            <div className="flex flex-wrap gap-1">
              <StatusBadge variant={health.datasetMode === "live" ? "neutral" : "warning"}>
                {health.datasetMode === "live"
                  ? "Live dataset"
                  : health.datasetMode === "partial"
                    ? "Partial dataset"
                    : "Demo dataset"}
              </StatusBadge>
              <StatusBadge variant={health.freshness === "fresh" ? "buy" : "warning"}>
                {health.freshness === "fresh" ? "Fresh data" : "Stale data"}
              </StatusBadge>
              <StatusBadge variant={health.validityState === "valid" ? "buy" : "warning"}>
                {health.validityState === "valid" ? "Valid dataset" : "Degraded dataset"}
              </StatusBadge>
              <StatusBadge variant={health.ingestState === "completed" ? "buy" : "warning"}>
                {health.ingestState === "completed"
                  ? "Completed ingest"
                  : health.ingestState === "incomplete"
                    ? "Incomplete ingest"
                    : "Failed ingest"}
              </StatusBadge>
            </div>

            <AuditGroup title="Manifest metadata">
              <AuditRow label="Schema version" value={manifest.schemaVersion} />
              <AuditRow label="Signal version" value={manifest.signalVersion} />
              <AuditRow
                label="Generated at"
                value={<time dateTime={manifest.generatedAt}>{manifest.generatedAt}</time>}
              />
              <AuditRow
                label="Stale after"
                value={<time dateTime={manifest.staleAfter}>{manifest.staleAfter}</time>}
              />
              <AuditRow label="Latest filing" value={formatDate(manifest.latestFilingProcessed)} />
              <AuditRow label="Dataset mode" value={manifest.datasetMode} />
              <AuditRow label="Freshness" value={health.freshness} />
              <AuditRow label="Validity" value={manifest.validityState} />
              <AuditRow label="Ingest" value={manifest.ingestState} />
            </AuditGroup>

            <AuditGroup title="Archive vs screener window">
              <AuditRow label="Export mode" value={manifest.coverage.exportMode} />
              <AuditRow
                label="Archive filing window"
                value={`${formatDate(manifest.coverage.archiveFilingWindowStart)} → ${formatDate(manifest.coverage.archiveFilingWindowEnd)}`}
              />
              <AuditRow
                label="Archive transactions"
                value={formatNumber(manifest.coverage.archiveTransactionCount)}
              />
              <AuditRow
                label="Screener filing window"
                value={`${formatDate(manifest.coverage.screenerFilingWindowStart)} → ${formatDate(manifest.coverage.screenerFilingWindowEnd)}`}
              />
              <AuditRow
                label="Screener transactions"
                value={formatNumber(manifest.coverage.screenerTransactionCount)}
              />
              <AuditRow
                label="Trusted live"
                value={health.isLiveTrusted ? "Yes" : "No"}
              />
            </AuditGroup>

            <AuditGroup title="Row and shard reconciliation">
              <AuditRow label="Manifest transactions" value={formatNumber(manifest.transactionCount)} />
              <AuditRow label="Loaded rows" value={formatNumber(loadedRowCount)} />
              <AuditRow label="Declared shards" value={formatNumber(manifest.transactionShardUrls.length)} />
              <AuditRow label="Exported shards" value={formatNumber(manifest.exportCounters.shards)} />
              <AuditRow label="Loaded shards" value={formatNumber(loadedShardCount)} />
            </AuditGroup>

            <AuditGroup title="Download, parse, export, and validation counters">
              <AuditRow label="Index rows seen" value={formatNumber(manifest.downloadCounters.indexRowsSeen)} />
              <AuditRow label="In-universe rows" value={formatNumber(manifest.downloadCounters.inUniverseRowsSeen)} />
              <AuditRow label="Downloaded filings" value={formatNumber(manifest.downloadCounters.downloadedFilings)} />
              <AuditRow label="Already present filings" value={formatNumber(manifest.downloadCounters.alreadyPresentFilings)} />
              <AuditRow label="Download errors" value={formatNumber(manifest.downloadCounters.errors)} />
              <AuditRow label="Parse errors" value={formatNumber(manifest.parseCounters.errors)} />
              <AuditRow label="Zero-transaction filings" value={formatNumber(manifest.parseCounters.zeroTransactionFilings)} />
              <AuditRow label="Exported transactions" value={formatNumber(manifest.exportCounters.transactions)} />
              <AuditRow label="Validation critical errors" value={formatNumber(manifest.validationCounters.criticalErrors)} />
              <AuditRow label="Validation warnings" value={formatNumber(manifest.validationCounters.warnings)} />
            </AuditGroup>

            <AuditGroup title="Universe coverage">
              <AuditRow label="Coverage mode" value={manifest.coverage.mode} />
              <AuditRow label="Universe tickers" value={formatNumber(manifest.coverage.tickerCount)} />
              <AuditRow label="Distinct issuer CIKs" value={formatNumber(manifest.coverage.cikCount)} />
              <AuditRow label="Filing window" value={`${formatDate(manifest.coverage.filingWindowStart)} - ${formatDate(manifest.coverage.filingWindowEnd)}`} />
              <AuditRow label="Trade window" value={`${formatDate(manifest.coverage.tradeWindowStart)} - ${formatDate(manifest.coverage.tradeWindowEnd)}`} />
              <AuditRow label="Duplicate index rows" value={formatNumber(manifest.coverage.duplicateIndexRows)} />
              <AuditRow label="Out-of-universe rows" value={formatNumber(manifest.coverage.outOfUniverseRows)} />
              {manifest.coverage.exchangeCoverage.map((exchange) => (
                <AuditRow
                  key={exchange.exchange}
                  label={formatExchangeLabel(exchange.exchange)}
                  value={`${formatNumber(exchange.tickerCount)} tickers / ${formatNumber(exchange.transactionCount)} trades`}
                />
              ))}
            </AuditGroup>

            <section className="rounded-md border border-border bg-surface-inset p-3">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Structured issues
              </h3>
              {health.validationIssues.length === 0 ? (
                <p className="text-xs text-muted-foreground">No structured validation issues.</p>
              ) : (
                <ul className="space-y-2 text-xs">
                  {health.validationIssues.map((issue) => (
                    <li key={`${issue.code}:${issue.message}`} className="rounded border border-warning bg-warning-soft p-2">
                      <div className="font-mono font-semibold text-warning">{issue.code}</div>
                      <div className="mt-1 text-foreground">{issue.message}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <p className="text-xs text-muted-foreground">{manifest.coverage.note}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
